// Content pieces: the article a content job produces, plus its image slots.
// Thin helpers over public.content_pieces / public.content_images.
//
// The generated Database type in supabase.ts does not know these tables yet
// (that typing lands with the migration), so this module talks to the client
// untyped and narrows results to the row types declared here.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { uniqueChannelName } from './realtime';
import {
  requestArticleDeploy,
  requestImageBuild,
  REBUILDABLE_STATUSES,
  type AiJob,
  type AiJobStatus,
} from './aiJobs';
import { AI_JOB_STATUS_LABEL } from './aiJobLabels';
import { ARTICLE_DEPLOYMENT_TITLE, type ClientKbFile } from './kb';
import { htmlToMarkdown } from './pieceMarkdown';

export type ContentPieceStatus = 'draft' | 'images_pending' | 'ready' | 'approved' | 'published';
export type ContentImageKind = 'featured' | 'inline';
export type ContentImageStatus = 'queued' | 'generating' | 'options_ready' | 'selected';
export type ContentImagePromptSource = 'agent' | 'manual';

/** `prompt` is per option — the CLI records one prompt per file. Options written before
 *  that change have none, and fall back to the slot's single `prompt`. */
export type ContentImageOption = {
  url: string;
  width?: number | null;
  height?: number | null;
  prompt?: string | null;
};
export type ContentImageAnchor = { after_block?: number; after_text?: string };

export type ContentPiece = {
  id: string;
  job_id: string;
  task_id: string | null;
  client_id: string | null;
  title: string;
  markdown: string;
  html: string;
  status: ContentPieceStatus;
  featured_image_id: string | null;
  version: number;
  created_at: string;
  updated_at: string;
};

export type ContentImage = {
  id: string;
  piece_id: string;
  slot_key: string;
  kind: ContentImageKind;
  anchor: ContentImageAnchor;
  context_excerpt: string;
  prompt: string | null;
  prompt_source: ContentImagePromptSource;
  model: string | null;
  status: ContentImageStatus;
  options: ContentImageOption[];
  selected_url: string | null;
  /**
   * SEO slug for the image subject, set by the image agent (`cockpit content images put
   * --filename`). Bare: no extension, no width suffix, no article-slug prefix. Names the
   * WebP download, and the deploying agent reuses it as the client-side media key.
   */
  filename: string | null;
  created_at: string;
  updated_at: string;
};

/** A piece with the joins the full-screen route needs. */
export type ContentPieceDetail = ContentPiece & {
  client_name: string | null;
  /** `brief` rides along so the SEO readout can measure against the keywords the brief asked for. */
  job: Pick<AiJob, 'id' | 'status' | 'title' | 'kind' | 'brief'> | null;
};

/** A row on the Content → Pieces table. */
export type ContentPieceListRow = ContentPiece & {
  client_name: string | null;
  image_count: number;
  images_pending: number;
};

export const FEATURED_SLOT_KEY = 'featured';

export const PIECE_STATUS_LABEL: Record<ContentPieceStatus, string> = {
  draft: 'Draft',
  images_pending: 'Images pending',
  ready: 'Ready',
  approved: 'Approved',
  published: 'Published',
};

export function pieceStatusTone(status: ContentPieceStatus): 'up' | 'warn' | 'info' | 'neutral' | 'accent' {
  switch (status) {
    case 'images_pending':
      return 'warn';
    case 'ready':
      return 'info';
    case 'approved':
    case 'published':
      return 'up';
    case 'draft':
    default:
      return 'neutral';
  }
}

export const IMAGE_STATUS_LABEL: Record<ContentImageStatus, string> = {
  queued: 'Queued',
  generating: 'Generating',
  options_ready: 'Options ready',
  selected: 'Selected',
};

/** Slots the queue still has to produce or the human still has to pick. */
export function isUnresolvedImage(image: ContentImage): boolean {
  return image.status !== 'selected';
}

export function newSlotKey(): string {
  const rand = Math.random().toString(36).slice(2, 7);
  return `img-${Date.now().toString(36)}${rand}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;
const db = supabase as unknown as LooseClient;

function pieces() {
  return db.from('content_pieces');
}

function images() {
  return db.from('content_images');
}

type PieceJoinRow = ContentPiece & {
  client?: { name: string } | { name: string }[] | null;
  job?: Pick<AiJob, 'id' | 'status' | 'title' | 'kind' | 'brief'> | Pick<AiJob, 'id' | 'status' | 'title' | 'kind' | 'brief'>[] | null;
  images?: { status: ContentImageStatus }[] | null;
};

function one<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function asImage(row: Record<string, unknown>): ContentImage {
  return {
    ...(row as ContentImage),
    anchor: (row.anchor as ContentImageAnchor) || {},
    options: Array.isArray(row.options) ? (row.options as ContentImageOption[]) : [],
  };
}

/* ---------- pieces ---------- */

export async function fetchPiece(id: string): Promise<ContentPieceDetail | null> {
  const { data, error } = await pieces()
    .select('*, client:clients(name), job:ai_jobs(id, status, title, kind, brief)')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const row = data as PieceJoinRow;
  return {
    ...(row as ContentPiece),
    client_name: one(row.client)?.name ?? null,
    job: one(row.job),
  };
}

/**
 * The client's other pieces, for the SEO readout's near-duplicate check.
 * Only the two columns that check needs — pulling every article's html for a
 * busy client is the one expensive thing on that tab, so it stays lazy.
 */
export async function fetchPieceLibrary(
  clientId: string,
  excludePieceId: string,
): Promise<{ title: string; html: string }[]> {
  const { data, error } = await pieces()
    .select('title, html')
    .eq('client_id', clientId)
    .neq('id', excludePieceId);
  if (error) throw error;
  return ((data ?? []) as { title: string; html: string }[]).filter((p) => p.html);
}

/** The piece a content job produced, if the writer has handed one in yet. */
export async function fetchPieceByJob(jobId: string): Promise<ContentPiece | null> {
  const { data, error } = await pieces().select('*').eq('job_id', jobId).maybeSingle();
  if (error) throw error;
  return (data as ContentPiece | null) ?? null;
}

export async function listPieces(options: { clientId?: string | null } = {}): Promise<ContentPieceListRow[]> {
  let query = pieces()
    .select('*, client:clients(name), images:content_images!piece_id(status)')
    .order('updated_at', { ascending: false });
  if (options.clientId) query = query.eq('client_id', options.clientId);
  const { data, error } = await query;
  if (error) throw error;
  return ((data ?? []) as PieceJoinRow[]).map((row) => {
    const imgs = row.images ?? [];
    const piece: Record<string, unknown> = { ...row };
    delete piece.client;
    delete piece.job;
    delete piece.images;
    return {
      ...(piece as ContentPiece),
      client_name: one(row.client)?.name ?? null,
      image_count: imgs.length,
      images_pending: imgs.filter((i) => i.status !== 'selected').length,
    };
  });
}

/**
 * Every html write on a piece comes through here — the editor's autosave and
 * all four image-slot operations. So this is the one place that has to keep the
 * markdown column in step. Regenerating markdown from the html on each save is
 * what stops it freezing at whatever `cockpit content put` first handed in.
 */
export async function updatePieceHtml(id: string, html: string): Promise<ContentPiece> {
  const { data, error } = await pieces()
    .update({ html, markdown: htmlToMarkdown(html) })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as ContentPiece;
}

export async function updatePieceStatus(id: string, status: ContentPieceStatus): Promise<ContentPiece> {
  const { data, error } = await pieces().update({ status }).eq('id', id).select('*').single();
  if (error) throw error;
  return data as ContentPiece;
}

export async function setFeaturedImage(id: string, imageId: string | null): Promise<ContentPiece> {
  const { data, error } = await pieces().update({ featured_image_id: imageId }).eq('id', id).select('*').single();
  if (error) throw error;
  return data as ContentPiece;
}

/* ---------- image slots ---------- */

export async function fetchImages(pieceId: string): Promise<ContentImage[]> {
  const { data, error } = await images()
    .select('*')
    .eq('piece_id', pieceId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as Record<string, unknown>[]).map(asImage);
}

export type InsertImageSlotInput = {
  piece_id: string;
  slot_key: string;
  kind: ContentImageKind;
  anchor: ContentImageAnchor;
  context_excerpt: string;
};

export async function insertImageSlot(input: InsertImageSlotInput): Promise<ContentImage> {
  const { data, error } = await images()
    .insert({ ...input, status: 'queued', prompt_source: 'agent', options: [] })
    .select('*')
    .single();
  if (error) throw error;
  return asImage(data as Record<string, unknown>);
}

export async function removeImageSlot(id: string): Promise<void> {
  const { error } = await images().delete().eq('id', id);
  if (error) throw error;
}

export type ImageSlotPatch = Partial<
  Pick<
    ContentImage,
    'status' | 'prompt' | 'prompt_source' | 'selected_url' | 'anchor' | 'context_excerpt' | 'model' | 'filename'
  >
>;

export async function updateImageSlot(id: string, patch: ImageSlotPatch): Promise<ContentImage> {
  const { data, error } = await images().update(patch).eq('id', id).select('*').single();
  if (error) throw error;
  return asImage(data as Record<string, unknown>);
}

/** "Use this one": pin an option on the slot. The caller swaps the marker in `html`. */
export async function selectImageOption(id: string, url: string): Promise<ContentImage> {
  return updateImageSlot(id, { status: 'selected', selected_url: url });
}

/** Re-queue one slot with a human-written prompt. Old options stay on the row. */
export async function submitManualPrompt(id: string, prompt: string): Promise<ContentImage> {
  const text = prompt.trim();
  if (!text) throw new Error('Write a prompt first');
  return updateImageSlot(id, { prompt: text, prompt_source: 'manual', status: 'queued' });
}

/* ---------- create images: hand the piece back to the build queue ---------- */

export type CreateImagesResult = { job: AiJob; piece: ContentPiece; slots: string[] };

/**
 * Spec 4.C "Create images":
 *  1. ai_jobs.model = xai/grok-4.6, brief.image_request = { piece_id, slots }
 *  2. build_ready → build_revision with an auto revision note
 *  3. piece status → images_pending
 */
export async function createImages(piece: ContentPiece, slots: ContentImage[]): Promise<CreateImagesResult> {
  const queued = slots.filter((s) => s.status === 'queued');
  if (queued.length === 0) throw new Error('Mark at least one image slot first');
  const keys = queued.map((s) => s.slot_key);
  const job = await requestImageBuild(piece.job_id, { piece_id: piece.id, slots: keys });
  const updated = await updatePieceStatus(piece.id, 'images_pending');
  return { job, piece: updated, slots: keys };
}

/**
 * Can this job be sent back for images right now?
 *
 * `build_ready` / `build_staged` are the job still sitting in the build queue.
 * `complete` / `submitted` are a finished job, and they belong here too: a piece
 * can be handed in, the job closed out, and the image slots marked afterwards —
 * which is exactly what happens when a human reads the draft before deciding
 * where pictures go. `requestImageBuild` already handles it, because
 * submitted → build_revision is the same legal transition "send back to build"
 * uses. This gate was simply narrower than the function it guards, so the
 * button went dead on a piece that was ready for images.
 */
export function canRequestImages(status: AiJobStatus | null | undefined): boolean {
  if (!status) return false;
  return status === 'build_ready' || status === 'build_staged' || REBUILDABLE_STATUSES.includes(status);
}

/* ---------- deploy: hand the finished article to the client's CMS ---------- */

export type DeployGate = {
  /** Null when Deploy is clickable. Otherwise the sentence the tooltip shows. */
  reason: string | null;
};

/**
 * Why Deploy is locked, in the order a human would fix it. The knowledge-file
 * check is the point of the gate: an article goes into a live client CMS, and
 * the agent doing it needs a written playbook for that client — which schema,
 * which model, where images and SEO go. No file, no deploy.
 *
 * `kb === undefined` means the lookup has not come back yet, which reads as
 * "still checking" rather than "missing".
 */
export function deployGate(
  piece: ContentPieceDetail | null,
  images: ContentImage[],
  kb: ClientKbFile | null | undefined
): DeployGate {
  if (!piece) return { reason: 'Loading this piece…' };
  if (piece.status === 'published') return { reason: 'Already deployed. Re-deploying is a rebuild on the job.' };
  if (!piece.client_id) return { reason: 'This piece has no client, so there is no CMS to deploy it to.' };

  const status = piece.job?.status ?? null;
  if (!status) return { reason: 'This piece has no queue job, so there is nothing to deploy from.' };
  if (!REBUILDABLE_STATUSES.includes(status)) {
    return {
      reason: `The job is ${AI_JOB_STATUS_LABEL[status]}. Deploy opens once the build is approved and the job is finished.`,
    };
  }

  const pending = images.filter(isUnresolvedImage).length;
  if (pending > 0) {
    return { reason: `${pending} image slot${pending === 1 ? '' : 's'} still need${pending === 1 ? 's' : ''} a pick.` };
  }

  if (kb === undefined) return { reason: 'Checking this client’s knowledge files…' };
  if (!kb) {
    return {
      reason: `${piece.client_name || 'This client'} has no current “${ARTICLE_DEPLOYMENT_TITLE}” knowledge file. Add one on the client’s Knowledge tab — it tells the agent how to get an article into their CMS.`,
    };
  }
  return { reason: null };
}

/**
 * Deploy: reopen the piece's own job as a build with `deploy_request` on the
 * brief. The piece is left alone — an agent sets it to `published` once the
 * article is actually live, so the status never claims more than what happened.
 */
export async function deployPiece(piece: ContentPieceDetail, kb: ClientKbFile): Promise<AiJob> {
  if (!piece.job) throw new Error('This piece has no queue job to deploy from');
  return requestArticleDeploy(piece.job.id, {
    piece_id: piece.id,
    piece_title: piece.title,
    kb_file_id: kb.id,
    kb_title: kb.title,
  });
}

/* ---------- realtime ---------- */

/** Fires on any change to the piece row or its image rows. */
export function subscribePiece(pieceId: string, onChange: () => void): () => void {
  const channel = supabase
    .channel(uniqueChannelName(`content_piece:${pieceId}`))
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'content_pieces', filter: `id=eq.${pieceId}` },
      onChange
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'content_images', filter: `piece_id=eq.${pieceId}` },
      onChange
    )
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}
